// drkClassicTheme.js
import { createTheme } from '@mui/material/styles';

// Blautöne für das modernisierte klassische Theme
const classicBlue = '#5B9BD5'; // Helleres, dezenteres Blau für Design-Elemente und Header
const classicLightBlue = '#9CC3E8'; // Hellere Variante für Hintergründe
const classicLightestBlue = 'rgba(91, 155, 213, 0.05)'; // Sehr helles Blau für Hintergründe

// Kontrastreichere Version für Buttons und Text
const classicButtonBlue = '#2E75B6'; // Dunkleres, kontrastreicheres Blau für Buttons/Text
const classicDarkBlue = '#1E5085'; // Noch dunklere Variante für Hover-Effekte

// DRK-Rot als Sekundärfarbe beibehalten
const drkRed = '#eb5b28';

// Kommunikationskanal-Farben
const channelColors = {
    email: {
        main: '#42a5f5', // Light blue
        light: 'rgba(66, 165, 245, 0.1)',
        dark: '#1976d2',
        contrastText: '#fff',
    },
    sms: {
        main: '#ffb74d', // Light amber
        light: 'rgba(255, 183, 77, 0.1)',
        dark: '#f57c00',
        contrastText: '#fff',
    },
    inapp: {
        main: '#66bb6a', // Light green
        light: 'rgba(102, 187, 106, 0.1)',
        dark: '#388e3c',
        contrastText: '#fff',
    },
    push: {
        main: '#ba68c8', // Light purple
        light: 'rgba(186, 104, 200, 0.1)',
        dark: '#7b1fa2',
        contrastText: '#fff',
    },
};

const drkClassicTheme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: classicButtonBlue, // Kontrastreicheres Blau für Buttons und interaktive Elemente
            light: classicLightBlue,
            dark: classicDarkBlue,
            contrastText: '#fff',
        },
        // Zusätzliche Palette für die dezenten Designelemente
        designBlue: {
            main: classicBlue, // Helleres Blau für Designelemente und Header
            light: classicLightBlue,
            dark: classicButtonBlue,
            contrastText: '#fff',
        },
        secondary: {
            main: drkRed,
            light: '#F8D7D3',
            dark: '#C74236',
            contrastText: '#fff',
        },
        error: {
            main: '#F44336',
        },
        background: {
            default: '#fff',
            paper: '#fff',
            accent: classicLightestBlue, // Analog zum modernen Theme
            muted: '#f5f5f5',
        },
        text: {
            primary: 'rgba(0, 0, 0, 0.87)',
            secondary: 'rgba(0, 0, 0, 0.6)',
            muted: 'rgba(0, 0, 0, 0.38)', // Hinzugefügt analog zum modernen Theme
        },
        appBlue: {
            main: classicBlue,
            light: classicLightBlue,
            dark: classicDarkBlue,
            contrastText: '#fff',
        },
        // Kommunikationskanal-Farben
        communicationChannels: channelColors,
    },
    typography: {
        fontFamily: [
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
        ].join(','),
        h4: {
            fontWeight: 700,
            color: drkRed,
        },
        h5: {
            fontWeight: 600,
            color: classicBlue, // Angepasst an die neue Primärfarbe
        },
        h6: {
            fontWeight: 500,
        },
        subtitle1: {
            fontWeight: 500,
        },
        colorHeader: {
            fontWeight: 600,
            color: 'white',
            fontSize: '1.5rem',
        },
    },
    shape: {
        borderRadius: 8,
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: 30, // Quelle des Problems für MenuItem
                    padding: '6px 16px',
                    boxShadow: '0 2px 6px rgba(46, 117, 182, 0.3)', // Angepasst an kontrastreichere Farbe
                    '&:hover': {
                        boxShadow: '0 3px 8px rgba(46, 117, 182, 0.4)', // Leicht erhöhter Schatten
                    },
                },
                containedPrimary: {
                    backgroundColor: classicButtonBlue, // Kontrastreicheres Blau für Buttons
                    '&:hover': {
                        backgroundColor: classicDarkBlue,
                    },
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    borderRadius: 0,
                    boxShadow: 'none',
                    textTransform: 'none',
                    padding: '6px 16px',
                    '&:hover': {
                        boxShadow: 'none',
                    },
                    '&.Mui-focusVisible': {
                    },
                    '&.Mui-selected': {
                        '&:hover': {
                        }
                    }
                },
                paper: {
                    borderRadius: 0,
                },
                list: {
                    padding: 0,
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 8,
                        '&:-webkit-autofill': {
                            WebkitBoxShadow: '0 0 0 1000px white inset',
                            WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                        },
                    },
                    '& .MuiInputLabel-root': {
                        // Verschiebt das Label für Icon-Platz
                        '&.MuiInputLabel-formControl': {
                            transform: 'translate(14px, 8px) scale(1)',
                        },
                        '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)',
                        },
                    },
                    // Spezielles Styling für Labels, wenn es ein Prefix/Icon gibt
                    '&.MuiTextField-hasIcon .MuiInputLabel-root': {
                        '&.MuiInputLabel-formControl': {
                            transform: 'translate(40px, 16px) scale(1)',
                        },
                        '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)',
                        },
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                    overflow: 'hidden', // Wichtig für den Header-Balken
                    position: 'relative',
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: 24,
                    '&:last-child': {
                        paddingBottom: 24,
                    },
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                },
                icon: {
                    alignItems: 'center',
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: classicButtonBlue, // Kontrastreicheres Blau für Links
                    textDecoration: 'none',
                    fontWeight: 500, // Angepasst an modernes Theme
                    '&:hover': {
                        textDecoration: 'underline',
                    },
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    margin: '16px 0', // Hinzugefügt analog zum modernen Theme
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                colorPrimary: {
                    backgroundColor: classicBlue, // Dezentes Blau für AppBar beibehalten
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 12, // Etwas abgerundeter wie im modernen Theme
                    overflow: 'hidden',
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    backgroundColor: classicBlue, // Dezentes Blau für Dialog-Header beibehalten
                    color: '#fff',
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                h5: {
                    color: classicButtonBlue, // Kontrastreicheres Blau für Überschriften
                },
                h6: {
                    color: classicButtonBlue, // Kontrastreicheres Blau für Überschriften
                },
            },
        }
    },
});

export default drkClassicTheme;
